const QUEUED_DIALOGUES = [
  "Waiting its turn",
  "Saved and lined up",
  "Next up for learning",
];

const INGESTING_DIALOGUES = {
  sql_expression: [
    "Learning this formula…",
    "Connecting the logic…",
    "Making sense of the SQL…",
    "Adding this to the playbook…",
    "Fitting the pieces together…",
  ],
  query_instruction: [
    "Studying this example…",
    "Matching question to query…",
    "Connecting words to SQL…",
    "Learning the route to an answer…",
    "Adding this pattern…",
  ],
  general_instruction: [
    "Taking in your guidance…",
    "Learning your ground rules…",
    "Tuning how Genei responds…",
    "Turning guidance into instinct…",
    "Getting aligned with your instructions…",
  ],
  table_metadata: [
    "Reading this table’s story…",
    "Learning what each column means…",
    "Connecting names to business meaning…",
    "Filing away the useful details…",
    "Making this table easier to understand…",
  ],
  metadata_sync: [
    "Taking inventory of your tables…",
    "Mapping the shape of your database…",
    "Reading tables and columns…",
    "Connecting the schema…",
    "Building a fresher map of your data…",
  ],
  source_cleanup: [
    "Clearing the old data map…",
    "Tidying up learned context…",
    "Removing outdated knowledge…",
    "Closing out this data source…",
    "Making room for what’s next…",
  ],
};

const FALLBACK_DIALOGUES = [
  "Learning this update…",
  "Connecting the details…",
  "Adding this to memory…",
  "Getting things in place…",
  "Finishing the background work…",
];

const toNullableString = (value) =>
  value === null || value === undefined ? null : String(value);

export function normalizeIngestionJob(raw = {}, metadata = {}) {
  return {
    jobId: toNullableString(raw.job_id ?? raw.jobId),
    status: String(raw.status || "queued"),
    resourceType: String(raw.resource_type ?? raw.resourceType ?? metadata.resourceType ?? ""),
    resourceId: toNullableString(raw.resource_id ?? raw.resourceId ?? metadata.resourceId),
    sourceDbId: toNullableString(raw.source_db_id ?? raw.sourceDbId ?? metadata.sourceDbId),
    operation: String(raw.operation || metadata.operation || "upsert"),
    progress: raw.progress || null,
    retryable: Boolean(raw.retryable),
    errorCode: raw.error_code ?? raw.errorCode ?? null,
    queuedAt: raw.queued_at ?? raw.queuedAt ?? null,
    updatedAt: raw.updated_at ?? raw.updatedAt ?? null,
    finishedAt: raw.finished_at ?? raw.finishedAt ?? null,
    label: metadata.label ?? raw.label ?? null,
    pollErrorCount: Number(metadata.pollErrorCount ?? raw.pollErrorCount ?? 0),
  };
}

export function nextDialogueIndex(length, currentIndex, randomValue = Math.random()) {
  if (length <= 1) return 0;
  const offset = 1 + Math.floor(randomValue * (length - 1));
  return (currentIndex + offset) % length;
}

function progressLabel(progress) {
  if (!progress || progress.total === null || progress.total === undefined) return null;
  return `${progress.completed} of ${progress.total}`;
}

export function getIngestionPresentation(job = {}, { compact = false } = {}) {
  const status = String(job.status || "queued");

  if (status === "failed") {
    return {
      tone: "error",
      stableLabel: job.retryable ? "Ingestion paused" : "Ingestion needs attention",
      visibleLabel: job.retryable ? "Learning paused" : "Needs attention",
      dialogues: [],
      progressLabel: null,
    };
  }

  if (status === "completed") {
    return {
      tone: "success",
      stableLabel: "Ingestion completed",
      visibleLabel: "Ready",
      dialogues: [],
      progressLabel: null,
    };
  }

  if (status === "superseded") {
    return {
      tone: "quiet",
      stableLabel: "Replaced by a newer update",
      visibleLabel: "Newer update in progress",
      dialogues: [],
      progressLabel: null,
    };
  }

  if (status === "queued") {
    return {
      tone: "queued",
      stableLabel: "Queued for ingestion",
      visibleLabel: QUEUED_DIALOGUES[0],
      dialogues: QUEUED_DIALOGUES,
      progressLabel: null,
    };
  }

  const dialogues = INGESTING_DIALOGUES[job.resourceType] || FALLBACK_DIALOGUES;
  return {
    tone: "ingesting",
    stableLabel: "Ingesting",
    visibleLabel: dialogues[0],
    dialogues: compact && job.resourceType === "sql_expression"
      ? dialogues.map((dialogue) => dialogue.replace("Adding this to the playbook…", "Adding to the playbook…"))
      : dialogues,
    progressLabel: progressLabel(job.progress),
  };
}

export const isInMotionStatus = (status) => ["queued", "ingesting"].includes(status);
export const isTerminalStatus = (status) => ["completed", "failed", "superseded"].includes(status);
export const ingestionJobKey = (resourceType, resourceId) =>
  `${String(resourceType || "unknown")}:${String(resourceId ?? "unknown")}`;
